import React, { useState } from "react";
import PropTypes from "prop-types";
import { kebabCase } from "lodash";
import styled, { css } from "styled-components";
import Icon from "components/icons";
import TimeSlotPill from "components/BusinessAvailabilityDisplay/TimeSlotPill";

const AvailabilityTimeContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  overflow: hidden;
  width: 100%;

  ${({ theme }) => theme.mq.lg`
    display:inherit;
    flex-wrap: nowrap;
  `}

  ${({ isExpanded }) =>
    isExpanded &&
    css`
      max-height: 1000px;
    `}
`;

const FullyBooked = styled.div`
  align-items: center;
  border: 1px ${({ theme }) => theme.themeColors.hoverSecondary} solid;
  border-radius: 0.75rem;
  color: ${({ theme }) => theme.themeColors.hoverSecondary};
  display: flex;
  font-size: ${({ theme }) => theme.fontSize(0)};
  font-weight: 600;
  height: 98%;
  justify-content: center;
  margin: 0.4375rem 0;
  height: 11.375rem;
  text-align: center;
  width: 4.6875rem;
`;

const MoreBttn = styled.button`
  background-color: ${({ theme }) => theme.colors.white};
  border: none;
  bottom: 0;
  color: ${({ theme }) => theme.themeColors.darkGreen3};
  display: none;
  font-size: ${({ theme }) => theme.fontSize(0)};
  height: 40px;
  line-height: 0.875rem;
  position: absolute;
  transition: all 0.3s;
  width: 100%;

  ${({ isActive, theme }) =>
      isActive &&
      css`
        background-color: ${theme.themeColors.lightGreen1};
      `}
    :hover {
    color: ${({ theme }) => theme.colors.darkGreen3};
  }

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 2px solid #0070f3;
  }

  ${({ theme }) => theme.mq.lg`
    display: block;
  `}
`;

const TimeSlotColumnWrapper = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  list-style-type: none;
  margin: 0;
  overflow: hidden;
  padding: 0;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.mq.lg`
    align-items: center;
    flex-direction: column;
    flex-wrap: nowrap;
    width: 100px;

    ${({ isDetail }) =>
      isDetail &&
      css`
        border: 0.125rem solid red;
      `}
  `}
`;

const EmptyAvailabiltyButton = styled.button`
  background-color: transparent;
  color: ${({ theme }) => theme.themeColors.secondary};
  border: 0.0625rem solid ${({ theme }) => theme.themeColors.secondary};
  border-radius: 1.3125rem;
  display: flex;
  font-size: 0.75rem;
  height: 2rem;
  justify-content: center;
  margin-top: 1rem;
  padding: 0.5rem;
  width: 100%;
  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 2px solid #0070f3;
  }

  :hover {
    color: ${({ theme }) => theme.themeColors.goodfriendDark};
    border: 0.0625rem solid ${({ theme }) => theme.themeColors.goodfriendDark};
  }
  ${({ theme }) => theme.mq.lg`
    margin-top: 0;
    width: 23.125rem;
  `}
`;
function TimeSlotsContainer({
  biz,
  dateArr,
  isActive,
  isCustomerUser,
  isDetail,
  isVendorUser,
  groupedTimeslots,
  isMobile,
  setShowChangeRoleModal,
  showCustomerModalSetPath,
  timePillLink,
  timePillQueryParams,
  tsStartTime,
  updateSelectedDay,
  timeZoneId,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const ts = biz?.attributes?.firstAvailableTimeslot;

  const hours = Math.floor(ts?.minutes_since_midnight_start / 60).toString();
  const minutes = (ts?.minutes_since_midnight_start % 60).toString();
  const nextAvailableDate = `${tsStartTime.month}/${tsStartTime.day}/${tsStartTime.year} `;
  const nextAvailabelTime = `${hours % 12 || 12}:${minutes.padStart(2, "0")} ${
    hours / 12 > 1 ? "PM" : "AM"
  }`;
  const nextAvailableText = `${nextAvailableDate} @ ${nextAvailabelTime}`;

  return (
    <AvailabilityTimeContainer isMobile={isMobile} isExpanded={isExpanded}>
      {dateArr.map((dateMilliseconds) => {
        // columnDay is a Timestamp
        const columnDay = new Date(dateMilliseconds);
        // key is an int for day of week (SUN is 0, MON is 1, etc.)
        const key = columnDay.getDay();
        // columnList is list of timeslot objects by day
        const columnList = groupedTimeslots[key] || [];
        const size = columnList.length;
        if (size <= 5) {
          for (let i = 0; i < 5 - size; i += 1) {
            columnList.push(0);
          }
        }

        if (size === 0 && isDetail) {
          return (
            <TimeSlotColumnWrapper
              key={`time-slot-wrapper-${biz.id}-${kebabCase(columnDay)}`}
            >
              <FullyBooked>FULLY BOOKED</FullyBooked>
            </TimeSlotColumnWrapper>
          );
        }

        return (
          <TimeSlotColumnWrapper
            key={`time-slot-wrapper-${biz.id}-${kebabCase(columnDay)}`}
            isMobile={isMobile}
          >
            {columnList[0] === 0 && isMobile && !isDetail ? (
              <EmptyAvailabiltyButton
                onClick={(e) => {
                  e.preventDefault();
                  updateSelectedDay(tsStartTime);
                }}
              >
                NEXT AVAILABLE DATE IS {nextAvailableText}
              </EmptyAvailabiltyButton>
            ) : (
              columnList.map((timeStamp, i) => {
                const columnItemIdx = `${i}`;
                const isHiddenTimeSlot = !isExpanded && size > 5 && i > 3;
                return (
                  <TimeSlotPill
                    apptDateInMsecs={dateMilliseconds?.ts}
                    bizId={biz?.id}
                    timeZoneId={timeZoneId}
                    columnDay={columnDay}
                    columnItemIdx={columnItemIdx}
                    isCustomerUser={isCustomerUser}
                    isDetail={isDetail}
                    isHiddenTimeSlot={isHiddenTimeSlot}
                    isVendorUser={isVendorUser}
                    setShowChangeRoleModal={setShowChangeRoleModal}
                    showCustomerModalSetPath={showCustomerModalSetPath}
                    timePillLink={timePillLink}
                    timePillQueryParams={timePillQueryParams}
                    timeStamp={timeStamp}
                  />
                );
              })
            )}
            {!isExpanded && size >= 6 && (
              <MoreBttn onClick={() => setIsExpanded(true)} isActive={isActive}>
                More <Icon name="chevron-12" color="#8F978C" />
              </MoreBttn>
            )}
          </TimeSlotColumnWrapper>
        );
      })}
    </AvailabilityTimeContainer>
  );
}

export default TimeSlotsContainer;

TimeSlotsContainer.propTypes = {
  biz: PropTypes.shape({
    attributes: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        businessName: PropTypes.string,
      }),
    ]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  dateArr: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ])
  ),
  groupedTimeslots: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({}),
      ])
    ),
  ]),
  isActive: PropTypes.bool,
  timeZoneId: PropTypes.string,
  isCustomerUser: PropTypes.bool,
  isDetail: PropTypes.bool,
  isVendorUser: PropTypes.bool,
  isMobile: PropTypes.bool,
  setShowChangeRoleModal: PropTypes.func,
  showCustomerModalSetPath: PropTypes.func,
  timePillLink: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  timePillQueryParams: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  tsStartTime: PropTypes.object.isRequired,
  updateSelectedDay: PropTypes.func,
};

TimeSlotsContainer.defaultProps = {
  biz: {
    attributes: {
      businessName: undefined,
    },
    id: null,
  },
  dateArr: [],
  groupedTimeslots: [],
  isActive: false,
  timeZoneId: null,
  isCustomerUser: false,
  isDetail: false,
  isVendorUser: false,
  isMobile: false,
  setShowChangeRoleModal: () => {},
  showCustomerModalSetPath: () => {},
  timePillLink: "/customer/booking/pets",
  timePillQueryParams: {},
  updateSelectedDay: undefined,
};
